import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Button,
} from '@mui/material';
import dayjs from 'dayjs';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import { getMessageDetail, updateMessage } from '../../endpoints/resources';
import { applications } from '../../utils/variables';
import { ArrowBack } from '@mui/icons-material';

export default function MessageDetail() {
  const { id } = useParams();
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const {
    data: messageData,
    isLoading,
    refetch,
  } = useApiGet(
    ['message-detail', id],
    getMessageDetail,
    {
      enabled: id ? true : false,
      retry: 1,
    },
    {
      id,
    }
  );

  const { mutate: updateMessageStatus } = useApiSend(
    updateMessage,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setIsSaving(false);
      setError(
        error.message
          ? error.message
          : 'Error updating message status, if problem persists, please contact support'
      );
    }
  );

  const handleApprove = () => {
    setIsSaving(true);
    updateMessageStatus({
      id: messageData.id,
      status: 'approved',
    });
  };
  const handleDeny = () => {
    setIsSaving(true);
    updateMessageStatus({
      id: messageData.id,
      status: 'denied',
    });
  };
  const handleSendForReview = () => {
    setIsSaving(true);
    updateMessageStatus({
      id: messageData.id,
      status: 'review',
    });
  };

  const isMessageAdmin = () => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    return apps.includes(applications.MessageAdmin);
  };

  const isMessageApprover = () => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    let messageUsers = [
      applications.MessageAdmin,
      applications.MessageApprover,
    ];
    return messageUsers.some((app) => apps.includes(app)) ? true : false;
  };

  const handleBacktoList = () => {
    window.location.href = '/message?refresh=true';
  };

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!messageData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <Button
          variant="text"
          color="black"
          onClick={() => handleBacktoList()}
          size="small"
          sx={{
            fontWeight: 'bold',
          }}
        >
          <ArrowBack sx={{ fontSize: 17, pr: 1 }} /> Back to List
        </Button>
      </Grid>
      <Grid item xs={12} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Email Request`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <strong>To:</strong> {messageData.to_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <strong>From:</strong> {messageData.from_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <strong>Created At:</strong>{' '}
                  {dayjs(messageData.created_at).format('MM/DD/YYYY HH:mm')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <strong>Status:</strong> {messageData.status}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <strong>Message Type:</strong> {messageData.message_type}
                </Typography>
              </Grid>
              {messageData.approved_by && (
                <Grid item xs={12} md={6} padding>
                  <Typography variant="body1">
                    <strong>Approved By:</strong> {messageData.approved_by}
                  </Typography>
                </Grid>
              )}
              {messageData.denied_by && (
                <Grid item xs={12} md={6} padding>
                  <Typography variant="body1">
                    <strong>Denied By:</strong> {messageData.denied_by}
                  </Typography>
                </Grid>
              )}
              {messageData.sent_at && (
                <Grid item xs={12} md={6} padding>
                  <Typography variant="body1">
                    <strong>Sent At:</strong>{' '}
                    {dayjs(messageData.sent_at).format('MM/DD/YYYY HH:mm')}
                  </Typography>
                </Grid>
              )}
              {error && (
                <Grid item xs={12} padding>
                  <Typography variant="body1" color="error">
                    {error}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={`Message Content`} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              <Grid item xs={12} padding>
                <Typography variant="body1">{messageData.message}</Typography>
              </Grid>
            </Grid>
          </CardContent>
          {messageData.status === 'Pending' && isMessageApprover() && (
            <CardActions sx={{ textAlign: 'right', padding: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isSaving}
                sx={{ fontWeight: 'bold' }}
                onClick={() => handleApprove()}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="black"
                size="small"
                disabled={isSaving}
                sx={{ fontWeight: 'bold' }}
                onClick={() => handleSendForReview()}
              >
                Send For Review
              </Button>
            </CardActions>
          )}
          {messageData.status === 'Under Review' && isMessageAdmin() && (
            <CardActions sx={{ textAlign: 'right', padding: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ fontWeight: 'bold' }}
                disabled={isSaving}
                onClick={() => handleApprove()}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="black"
                size="small"
                disabled={isSaving}
                sx={{ fontWeight: 'bold' }}
                onClick={() => handleDeny()}
              >
                Deny
              </Button>
            </CardActions>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}
